import { useEffect, useState } from "react";
import { Autocomplete, Card, Grid, Icon, Skeleton, Chip, Divider, Checkbox, FormControlLabel, TablePagination, List, ListItemText, ListItemButton, Paper, ListItemAvatar, Avatar, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Collapse, IconButton, MenuItem } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import MDDatePicker from "components/MDDatePicker";

import { get, postNoResponse, post, postUpload, getFile } from "utils/WebService";
import { errorAlert, successAlert, infoAviso } from "utils/Functions";
import { useUser } from "context/UserContext";
import { useMaterialUIController } from "context";
import logoRal from "assets/images/logos/logo.png";

const TIPOS_DOCUMENTO = [
    { id: 1, label: "Pesquisa Receita Federal" },
    { id: 2, label: "Pesquisa Web" },
    { id: 3, label: "Pesquisa Unitfour" },
    { id: 4, label: "Pesquisa API" },
    { id: 5, label: "Contrato / Termo" },
    { id: 6, label: "Comprovante / Recibo" },
    { id: 7, label: "Procuração" },
    { id: 8, label: "Anexo de Ocorrência" },
    { id: 99, label: "Outros" }
];

export default function Historico({ recordId, idDevedorCarteira, isViewOnly, preloadedDevedor, preloadedHistoricos, refreshKey }) {
    const GroupComponent = ({ params, children, darkMode }) => {
        const [open, setOpen] = useState(false);
        return (
            <li key={params.key}>
                <div 
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpen(!open);
                    }}
                    style={{ 
                        fontWeight: 'bold', 
                        padding: '8px 16px', 
                        cursor: 'pointer', 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: darkMode ? '#4B4F5C' : '#f0f2f5',
                        borderBottom: '1px solid rgba(0,0,0,0.1)'
                    }}
                >
                    <MDTypography variant="caption" fontWeight="bold" color={darkMode ? "white" : "text"}>{params.group}</MDTypography>
                    <Icon sx={{ color: darkMode ? "#fff" : "inherit" }}>{open ? "expand_less" : "expand_more"}</Icon>
                </div>
                <Collapse in={open}>
                    <ul style={{ padding: 0, margin: 0 }}>{children}</ul>
                </Collapse>
            </li>
        );
    };

    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const { userPerfils } = useUser();
    const PERFIS_PERMITIDOS_EXCLUSAO = [1, 3, 4, 6]; // 1: Gestor, 3: Coordenador, 4: Administrador, 6: Suporte

    const isAdminOrSuporte = userPerfils && userPerfils.some(p => {
        if (typeof p === 'number') return p === 4 || p === 6;
        const tipo = typeof p === 'object' ? (p?.tipoPerfil ?? p?.cvTipo ?? p?.cvCodPerfil ?? p?.id ?? p?.code) : null;
        if (tipo !== null && tipo !== undefined && (Number(tipo) === 4 || Number(tipo) === 6)) return true;
        const name = (typeof p === 'string' ? p : (p?.nome || p?.ccNome || p?.label || '')).toLowerCase();
        return name.includes("admin") || name.includes("suporte");
    });

    const isCoordenadorOrGestor = userPerfils && userPerfils.some(p => {
        if (typeof p === 'number') return p === 1 || p === 3;
        const tipo = typeof p === 'object' ? (p?.tipoPerfil ?? p?.cvTipo ?? p?.cvCodPerfil ?? p?.id ?? p?.code) : null;
        if (tipo !== null && tipo !== undefined && (Number(tipo) === 1 || Number(tipo) === 3)) return true;
        const name = (typeof p === 'string' ? p : (p?.nome || p?.ccNome || p?.label || '')).toLowerCase();
        return name.includes("coord") || name.includes("gestor");
    });

    const isSupervisor = userPerfils && userPerfils.some(p => {
        if (typeof p === 'number') return p === 7;
        const tipo = typeof p === 'object' ? (p?.tipoPerfil ?? p?.cvTipo ?? p?.cvCodPerfil ?? p?.id ?? p?.code) : null;
        if (tipo !== null && tipo !== undefined && Number(tipo) === 7) return true;
        const name = (typeof p === 'string' ? p : (p?.nome || p?.ccNome || p?.label || '')).toLowerCase();
        return name.includes("superv");
    });

    const podeExcluirOcorrencia = userPerfils && userPerfils.some(p => {
        if (typeof p === 'number') return PERFIS_PERMITIDOS_EXCLUSAO.includes(p);
        const tipo = typeof p === 'object' ? (p?.tipoPerfil ?? p?.cvTipo ?? p?.cvCodPerfil ?? p?.id ?? p?.code) : null;
        if (tipo !== null && tipo !== undefined && PERFIS_PERMITIDOS_EXCLUSAO.includes(Number(tipo))) return true;
        const name = (typeof p === 'string' ? p : (p?.nome || p?.ccNome || p?.label || '')).toLowerCase();
        return name.includes("admin") || name.includes("gestor") || name.includes("coord") || name.includes("suporte");
    });
    const canSeeRelatorioIA = isAdminOrSuporte || isCoordenadorOrGestor || isSupervisor;
    const podeRegredirStatus = isAdminOrSuporte || isCoordenadorOrGestor || isSupervisor;
    const [historico, setHistorico] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtro, setFiltro] = useState("TODOS");
    
    const [dataInicio, setDataInicio] = useState("");
    const [dataFim, setDataFim] = useState("");
    const OPTION_ALL_EQUIPES = { id: "all", nome: "Sem Filtro" };
    const [equipesList, setEquipesList] = useState([]);
    const [selectedEquipeFiltro, setSelectedEquipeFiltro] = useState(OPTION_ALL_EQUIPES);
    const [filtroTipo, setFiltroTipo] = useState(null);
    const [filtroImportacao, setFiltroImportacao] = useState(false);

    useEffect(() => {
        get("/users/coordenador/equipes")
            .then((res) => {
                if (Array.isArray(res) && res.length > 0) {
                    setEquipesList(res);
                } else {
                    get("/equipe/list-all").then(res2 => {
                        const list = res2?.list || res2?.data || (Array.isArray(res2) ? res2 : []);
                        setEquipesList(list.map(e => ({ id: e.id || e.cvId, nome: e.nome || e.ccNome })));
                    }).catch(() => {});
                }
            })
            .catch(() => {
                get("/equipe/list-all").then(res2 => {
                    const list = res2?.list || res2?.data || (Array.isArray(res2) ? res2 : []);
                    setEquipesList(list.map(e => ({ id: e.id || e.cvId, nome: e.nome || e.ccNome })));
                }).catch(() => {});
            });
    }, []);
    
    const [tiposOcorrencia, setTiposOcorrencia] = useState([]);
    const [selectedTipo, setSelectedTipo] = useState(null);
    const [selectedTipoDoc, setSelectedTipoDoc] = useState(8);
    const [novaOcorrencia, setNovaOcorrencia] = useState("");
    const [anexosOcorrencia, setAnexosOcorrencia] = useState([]);
    const [dataAgendamento, setDataAgendamento] = useState("");
    const [salvando, setSalvando] = useState(false);
    
    // Novos campos de acionamento
    const [devedorData, setDevedorData] = useState(null);
    const [telefoneContato, setTelefoneContato] = useState("");
    const [emailContato, setEmailContato] = useState("");
    const [nomeResponsavel, setNomeResponsavel] = useState("");
    const [funcaoResponsavel, setFuncaoResponsavel] = useState("");
    const [semDadosResponsavel, setSemDadosResponsavel] = useState(false);
    
    const [statusList, setStatusList] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState(null);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedItem, setSelectedItem] = useState(null);

    const [openResumo, setOpenResumo] = useState(false);
    const [resumoText, setResumoText] = useState("");
    const [generatingAi, setGeneratingAi] = useState(false);
    const [documentosFicha, setDocumentosFicha] = useState([]);

    const fetchDocumentosFicha = () => {
        if (!recordId) return;
        get(`/dossie/list/${recordId}`)
            .then((res) => { if (Array.isArray(res)) setDocumentosFicha(res); })
            .catch(() => {});
    };

    useEffect(() => {
        if (recordId) fetchDocumentosFicha();
    }, [recordId]);

    useEffect(() => {
        setPage(0);
        setSelectedItem(null);
    }, [filtro, dataInicio, dataFim, filtroTipo, selectedEquipeFiltro, filtroImportacao]);

    const fetchHistorico = (forceApi = false) => {
        if (!recordId) {
            setLoading(false);
            return;
        }
        
        if (!forceApi && Array.isArray(preloadedHistoricos) && preloadedHistoricos.length > 0) {
            let list = preloadedHistoricos;
            if (idDevedorCarteira) {
                // Mantém todas as ocorrências do devedor visíveis para operadores e admins
                setHistorico(list);
            } else {
                setHistorico(list);
            }
            setLoading(false);
            return;
        }

        setLoading(true);
        get(`/devedor/historico/${recordId}`)
            .then(res => {
                let list = res || [];
                setHistorico(list);
            })
            .catch(() => {
                errorAlert("Erro ao carregar histórico");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        if (!recordId) return;

        const normalizeString = (str) => {
            if (!str) return "";
            return str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        };

        setLoading(true);
        
        let promiseDevedor = preloadedDevedor !== undefined 
            ? Promise.resolve(preloadedDevedor) 
            : get(`/devedor/by-id/${recordId}`);

        Promise.all([
            post("/status-ficha/list", { page: 0, pageSize: 1000, sorted: [], filtered: [] }),
            promiseDevedor,
            post("/tipo-ocorrencia/list", { page: 0, pageSize: 1000, sorted: [], filtered: [] })
        ]).then(([statusRes, devedorRes, tiposRes]) => {
            let sList = statusRes?.list || statusRes?.data || (Array.isArray(statusRes) ? statusRes : []);
            sList = sList.filter(s => s && s.cvAtivo !== false && s.cvIndSistema !== true).sort((a, b) => String(a?.ccNome || a?.nome || "").localeCompare(String(b?.ccNome || b?.nome || "")));
            setStatusList(sList);

            let targetStatusId = devedorRes?.idStatusFicha;
            let targetStatusNome = devedorRes?.statusFichaNome || devedorRes?.nomeStatusFicha;

            if (idDevedorCarteira && devedorRes?.fichas) {
                const targetFicha = devedorRes.fichas.find(f => f.id === idDevedorCarteira || f.idDevedorCarteira === idDevedorCarteira);
                if (targetFicha) {
                    if (targetFicha.idStatusFicha) targetStatusId = targetFicha.idStatusFicha;
                    if (targetFicha.statusFichaNome || targetFicha.nomeStatusFicha) {
                        targetStatusNome = targetFicha.statusFichaNome || targetFicha.nomeStatusFicha;
                    }
                }
            } else if (!targetStatusId && !targetStatusNome && devedorRes?.fichas && devedorRes.fichas.length > 0) {
                const firstFicha = devedorRes.fichas[0];
                if (firstFicha.idStatusFicha) targetStatusId = firstFicha.idStatusFicha;
                if (firstFicha.statusFichaNome || firstFicha.nomeStatusFicha) {
                    targetStatusNome = firstFicha.statusFichaNome || firstFicha.nomeStatusFicha;
                }
            }

            if (targetStatusId || targetStatusNome) {
                const currentStatus = sList.find(s => s && (
                    (targetStatusId && (s.id === targetStatusId || String(s.cvId) === String(targetStatusId))) ||
                    (targetStatusNome && (s.ccNome === targetStatusNome || s.nome === targetStatusNome))
                ));
                setSelectedStatus(currentStatus || null);
            } else {
                setSelectedStatus(null);
            }
            setDevedorData(devedorRes);

            const list = tiposRes?.list || tiposRes?.data || (Array.isArray(tiposRes) ? tiposRes : []);
            const systemTypesNormalized = [
                "importacao do sistema", 
                "atualizacao de status", 
                "atualizacao cadastral", 
                "atualizacao de cadastro",
                "acordo (sistema)", 
                "contrato (sistema)",
                "pagamento de parcela",
                "pagamento avulso"
            ];
            const isAdmin = userPerfils && userPerfils.some(p => p?.tipoPerfil === 6 || p?.nome === 'Administrador' || p === 'Administrador');
            const isSuporte = userPerfils && userPerfils.some(p => p?.tipoPerfil === 4 || p?.nome === 'Suporte' || p === 'Suporte');

            const processedTipos = list.filter(t => {
                if (!t || !t.nome || t.ativo === false) return false;
                if (systemTypesNormalized.includes(normalizeString(t.nome))) return false;
                
                const cat = t.categoria || 1;
                
                // Categorias 2 (Alteração), 3 (Importação) e 5 (Sistema) são automáticas e não devem aparecer nas listas de registro manual
                if (cat === 2 || cat === 3 || cat === 5) return false;

                if (isAdmin) return true;
                if (isSuporte) return cat === 1 || cat === 4;
                return cat === 1 || !t.categoria;
            }).map(t => {
                let grupoLabel = "Ocorrência"; // Fallback apenas visual de segurança
                if (t.categoria === 1) grupoLabel = "Ocorrência";
                else if (t.categoria === 4) grupoLabel = "Suporte";
                return { ...t, grupoLabel };
            });

            setTiposOcorrencia(processedTipos);
        }).catch(err => {
            console.error("Erro ao carregar dados do histórico", err);
            errorAlert("Erro ao carregar dados do histórico");
        }).finally(() => {
            setLoading(false);
        });

        fetchHistorico();
    }, [recordId, refreshKey, idDevedorCarteira, preloadedDevedor, preloadedHistoricos]);

    useEffect(() => {
        if (selectedTipo && selectedTipo.nome) {
            const nomeLower = selectedTipo.nome.toLowerCase();
            if (nomeLower.includes("acordo") || nomeLower.includes("contrato") || nomeLower.includes("termo")) setSelectedTipoDoc(5);
            else if (nomeLower.includes("pagamento") || nomeLower.includes("comprovante") || nomeLower.includes("recibo") || nomeLower.includes("boleto")) setSelectedTipoDoc(6);
            else if (nomeLower.includes("procuração") || nomeLower.includes("procuracao") || nomeLower.includes("mandato")) setSelectedTipoDoc(7);
            else if (nomeLower.includes("receita federal")) setSelectedTipoDoc(1);
            else if (nomeLower.includes("pesquisa web")) setSelectedTipoDoc(2);
            else if (nomeLower.includes("unitfour")) setSelectedTipoDoc(3);
            else if (nomeLower.includes("pesquisa api")) setSelectedTipoDoc(4);
            else if (nomeLower.includes("pesquisa")) setSelectedTipoDoc(8); // Default de anexo se for pesquisa genérica
            else setSelectedTipoDoc(8);
        } else {
            setSelectedTipoDoc(8);
        }
    }, [selectedTipo]);

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const strEmail = String(email).toLowerCase();
        if (!re.test(strEmail)) return false;
        if (strEmail.endsWith("@gamil.com")) return false;
        if (strEmail.endsWith("@hotmai.com") || strEmail.endsWith("@hotamil.com") || strEmail.endsWith("@outlok.com")) return false;
        return true;
    };

    const handleSalvarOcorrencia = () => {
        if (!recordId) return;

        if (emailContato && !validateEmail(emailContato)) {
            errorAlert("O e-mail informado é inválido.");
            return;
        }

        setSalvando(true);
        
        let finalObservacao = novaOcorrencia;
        
        let cabecalho = "";
        let contatosArr = [];
        if (telefoneContato) contatosArr.push(`Tel: ${telefoneContato}`);
        if (emailContato) contatosArr.push(`E-mail: ${emailContato}`);
        if (contatosArr.length > 0) {
            cabecalho += `Contato: ${contatosArr.join(' / ')}\n`;
        }
        if (semDadosResponsavel) {
            cabecalho += `Responsável: Sem dados\n`;
        } else if (nomeResponsavel || funcaoResponsavel) {
            cabecalho += `Responsável: ${nomeResponsavel || 'Não informado'} ${funcaoResponsavel ? `(${funcaoResponsavel})` : ''}\n`;
        }
        
        if (cabecalho) {
            finalObservacao = cabecalho + "\n" + finalObservacao;
        }

        let dddContatoStr = null;
        let telefoneContatoStr = null;
        if (telefoneContato) {
            const numOnly = telefoneContato.replace(/\D/g, '');
            if (numOnly.length >= 10) {
                dddContatoStr = numOnly.substring(0, 2);
                telefoneContatoStr = numOnly.substring(2);
            } else {
                telefoneContatoStr = telefoneContato;
            }
        }

        const payload = {
            observacao: finalObservacao,
            tipoOcorrenciaId: selectedTipo ? selectedTipo.id : null,
            statusFichaId: selectedStatus ? selectedStatus.id : null,
            dataAgendamento: (selectedTipo && selectedTipo.permiteAgendamento && dataAgendamento) ? `${dataAgendamento}:00` : null,
            dddContato: dddContatoStr,
            telefoneContato: telefoneContatoStr,
            emailContato: emailContato,
            nomeContato: nomeResponsavel,
            idDevedorCarteira: idDevedorCarteira || null
        };
        
        const savePromise = post(`/devedor/historico/add/${recordId}`, payload);

        if (anexosOcorrencia && anexosOcorrencia.length > 0) {
            savePromise
                .then((res) => {
                    const uploadPromises = anexosOcorrencia.map(file => {
                        const formData = new FormData();
                        formData.append("file", file);
                        formData.append("tipoDocumentoId", selectedTipoDoc); // Usa a regra inteligente
                        formData.append("observacao", `Anexo da ocorrência registrada por ${nomeResponsavel || 'operador'}`);
                        if (res && res.idHistorico) {
                            formData.append("idHistorico", res.idHistorico);
                        }
                        return postUpload(`/dossie/upload/${recordId}`, formData);
                    });
                    return Promise.all(uploadPromises);
                })
                .then(() => {
                    successAlert(`Ocorrência registrada com ${anexosOcorrencia.length} anexo(s)!`);
                    setNovaOcorrencia("");
                    setAnexosOcorrencia([]);
                    setSelectedTipo(null);
                    setDataAgendamento("");
                    setTelefoneContato("");
                    setEmailContato("");
                    setNomeResponsavel("");
                    setFuncaoResponsavel("");
                    setSemDadosResponsavel(false);
                    fetchHistorico(true);
                    fetchDocumentosFicha();
                    get(`/devedor/by-id/${recordId}`).then(res => {
                        if (res) setDevedorData(res);
                    }).catch(() => {});
                    window.dispatchEvent(new CustomEvent("devedorStatusUpdated"));
                    window.dispatchEvent(new CustomEvent("devedorDocumentoUpdated"));
                })
                .catch(() => errorAlert("Erro ao salvar ocorrência com anexos"))
                .finally(() => setSalvando(false));
        } else {
            savePromise
                .then(() => {
                    successAlert("Ocorrência registrada!");
                    setNovaOcorrencia("");
                    setAnexosOcorrencia([]);
                    setSelectedTipo(null);
                    setDataAgendamento("");
                    setTelefoneContato("");
                    setEmailContato("");
                    setNomeResponsavel("");
                    setFuncaoResponsavel("");
                    setSemDadosResponsavel(false);
                    fetchHistorico(true);
                    get(`/devedor/by-id/${recordId}`).then(res => {
                        if (res) setDevedorData(res);
                    }).catch(() => {});
                    window.dispatchEvent(new CustomEvent("devedorStatusUpdated"));
                })
                .catch(() => errorAlert("Erro ao salvar ocorrência"))
                .finally(() => setSalvando(false));
        }
    };

    const handleDeleteOcorrencia = (idOcorrencia) => {
        infoAviso(
            "Tem certeza que deseja excluir esta ocorrência?",
            () => {
                postNoResponse(`/devedor/historico/delete/${idOcorrencia}`)
                    .then(() => {
                        successAlert("Ocorrência excluída!");
                        if (selectedItem && selectedItem.id === idOcorrencia) {
                            setSelectedItem(null);
                        }
                        fetchHistorico(true);
                    })
                    .catch(() => errorAlert("Erro ao excluir ocorrência"));
            }
        );
    };

    const getBusinessDate = (startDate, daysToAdd) => {
        let currentDate = new Date(startDate);
        let addedDays = 0;
        while (addedDays < daysToAdd) {
            currentDate.setDate(currentDate.getDate() + 1);
            if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
                addedDays++;
            }
        }
        return currentDate;
    };

    const formatDateTimeForInput = (date, isEndOfDay = false) => {
        const localDate = new Date(date);
        if (isEndOfDay) {
            localDate.setHours(23, 59, 0, 0);
        }
        const offset = localDate.getTimezoneOffset();
        const adjustedDate = new Date(localDate.getTime() - (offset*60*1000));
        return adjustedDate.toISOString().slice(0, 16);
    };

    const hoje = new Date();
    const minDateStr = formatDateTimeForInput(hoje);
    const maxDate = (selectedTipo && selectedTipo.permiteAgendamento) ? getBusinessDate(hoje, selectedTipo.prazoMaxAgendamento || 0) : hoje;
    const maxDateStr = formatDateTimeForInput(maxDate, true);

    const isFormValid = () => {
        if (!selectedTipo || !selectedStatus || salvando || !recordId || !novaOcorrencia) return false;
        if (selectedTipo.permiteAgendamento && !dataAgendamento) return false;
        
        const lowerStatus = selectedStatus?.nome?.toLowerCase() || selectedStatus?.ccNome?.toLowerCase() || '';
        const isPesquisaStatus = lowerStatus.includes('pesquisa') || lowerStatus.includes('não localizado') || lowerStatus.includes('nao localizado');
        if (!isPesquisaStatus && !telefoneContato && !emailContato) return false;
        
        return true;
    };

    const getAvailableTelefones = () => {
        if (!devedorData || !devedorData.telefones) return [];
        return [...new Set(devedorData.telefones.map(t => {
            const ddd = t.ccDdd ? `(${t.ccDdd}) ` : "";
            const tel = t.ccTelefone || "";
            return tel ? `${ddd}${tel}` : null;
        }).filter(Boolean))];
    };
    const availableTelefones = getAvailableTelefones();

    const getAvailableEmails = () => {
        if (!devedorData || !devedorData.emails) return [];
        return [...new Set(devedorData.emails.map(e => e.ccEmail).filter(Boolean))];
    };
    const availableEmails = getAvailableEmails();

    const currentStatusId = devedorData?.idStatusFicha;
    const currentStatusObj = currentStatusId ? statusList.find(s => s.id === currentStatusId) : null;
    const forcaAtual = currentStatusObj ? (currentStatusObj.forca || 0) : 0;

    const availableStatusList = statusList;

    const availableTiposOcorrencia = tiposOcorrencia.map(tipo => {
        return {
            ...tipo,
            grupoOrder: tipo.categoria || 99,
            finalGrupoLabel: tipo.grupoLabel || "Ocorrência"
        };
    }).sort((a, b) => {
        if (a.grupoOrder !== b.grupoOrder) return a.grupoOrder - b.grupoOrder;
        return (a.nome || "").localeCompare(b.nome || "");
    });

    const filteredHistorico = historico.filter(item => {
        if (filtroImportacao) {
            if (!item.situacaoLabel?.toLowerCase().includes("importação")) return false;
        } else {
            if (filtro === "ALTERACAO" && item.cor !== "info" && item.cor !== "secondary") return false;
            if (filtro === "OCORRENCIA" && (item.cor === "info" || item.cor === "warning" || item.cor === "secondary")) return false;
            if (filtro === "TODOS" && item.situacaoLabel?.toLowerCase().includes("importação")) return false; // Hide importations from TODOS by default
        }
        
        if (dataInicio) {
            const dataItem = new Date(item.dataHora);
            const dataIni = new Date(dataInicio + "T00:00:00");
            if (dataItem < dataIni) return false;
        }
        
        if (dataFim) {
            const dataItem = new Date(item.dataHora);
            const dataF = new Date(dataFim + "T23:59:59");
            if (dataItem > dataF) return false;
        }
        
        if (filtroTipo && filtroTipo.nome) {
            if (item.situacaoLabel !== filtroTipo.nome) return false;
        }

        if (selectedEquipeFiltro && selectedEquipeFiltro.id !== "all") {
            const userName = (item.nomeUsuario || "").toLowerCase();
            
            // Ocorrências do Sistema ou sem usuário só aparecem no "Sem Filtro"
            if (!userName || userName === "sistema" || userName === "robot") {
                return false;
            }

            const targetEquipeNome = (selectedEquipeFiltro.nome || selectedEquipeFiltro.ccNome || "").toLowerCase();
            const simpleTarget = targetEquipeNome.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

            // 1. Checagem direta se a ocorrência pertence à equipe selecionada
            if (item.nomeEquipe) {
                const simpleItemEquipe = item.nomeEquipe.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                if (simpleItemEquipe.includes(simpleTarget) || simpleTarget.includes(simpleItemEquipe)) {
                    return true;
                }
            }

            const simpleUserName = userName.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
            const uTokens = simpleUserName.split(/\s+/).filter(Boolean);

            const matchMember = (m) => {
                const mName = (m.nome || m.ccNome || m.usuario || "").toLowerCase();
                const simpleMemberName = mName.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                if (simpleMemberName.includes(simpleUserName) || simpleUserName.includes(simpleMemberName)) {
                    return true;
                }
                const mTokens = simpleMemberName.split(/\s+/).filter(Boolean);
                if (mTokens.length > 0 && uTokens.length > 0 && mTokens[0] === uTokens[0]) {
                    if (mTokens.some(t => t.length > 3 && uTokens.includes(t))) return true;
                }
                return false;
            };

            // 2. Checagem por membros da equipe selecionada (Operadores, Backoffice, Coordenadores, Supervisores)
            if (Array.isArray(selectedEquipeFiltro.membros) && selectedEquipeFiltro.membros.length > 0) {
                const isMemberOfSelectedTeam = selectedEquipeFiltro.membros.some(matchMember);
                if (isMemberOfSelectedTeam) {
                    return true;
                }
            }

            // Se o usuário pertence a outra equipe ou não faz parte da equipe selecionada, oculta do filtro
            return false;
        }

        return true;
    });

    const getColorBySituacao = (item) => {
        if (item.situacaoLabel?.toLowerCase().includes("acordo") || item.situacaoLabel?.toLowerCase().includes("pagamento de parcela")) return "secondary";
        if (item.situacaoLabel?.toLowerCase().includes("pagamento avulso")) return "success";
        if (item.cor) return item.cor;
        return "success";
    }

    const getIconBySituacao = (item) => {
        if (item.situacaoLabel?.toLowerCase().includes("acordo") || item.situacaoLabel?.toLowerCase().includes("pagamento de parcela")) return "handshake";
        if (item.situacaoLabel?.toLowerCase().includes("pagamento avulso")) return "payments";
        if (item.cor === "info") return "edit";
        if (item.cor === "warning") return "cloud_download";
        return "chat"; 
    }

    const formatarData = (dataStr) => {
        if (!dataStr) return "";
        const date = new Date(dataStr);
        return date.toLocaleString('pt-BR');
    }

    const renderObservacao = (text) => {
        if (!text) return <MDTypography variant="caption" color="text">Sem observações.</MDTypography>;
        const lines = text.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
        return (
            <MDBox display="flex" flexDirection="column" gap={0.2}>
                {lines.map((line, idx) => {
                    const isSectionHeader = line.includes("Detalhe das parcelas") || line.includes("Contratos do Acordo") || line.includes("Acordo Formalizado") || line.endsWith(":");
                    return (
                        <MDTypography
                            key={idx}
                            variant="caption"
                            fontWeight={isSectionHeader ? "bold" : "regular"}
                            color={isSectionHeader ? "dark" : "text"}
                            sx={{ 
                                lineHeight: 1.15,
                                mt: (isSectionHeader && idx > 0) ? 0.8 : 0
                            }}
                        >
                            {line}
                        </MDTypography>
                    );
                })}
            </MDBox>
        );
    };

    const handleOpenResumo = () => {
        const userOcorrencias = historico.filter(item => item.nomeUsuario && item.nomeUsuario.toLowerCase() !== "sistema" && item.nomeUsuario.toLowerCase() !== "robot" && !item.situacaoLabel?.toLowerCase().includes("importação"));
        
        if (userOcorrencias.length === 0) {
            errorAlert("Não há ocorrências de usuários para resumir.");
            return;
        }
        
        let text = "";
        userOcorrencias.forEach(item => {
            text += `[${formatarData(item.dataHora)}] ${item.nomeUsuario} - ${item.situacaoLabel || 'Ocorrência'}\n`;
            if (item.observacao) {
                text += `${item.observacao}\n`;
            }
            text += `----------------------------------------\n`;
        });
        
        setResumoText(text);
        setOpenResumo(true);
    };

    const handleExportResumo = () => {
        const printWindow = window.open('', '', 'width=800,height=600');
        if (!printWindow) return;
        
        let formattedTextHtml = resumoText
            .replace(/\n/g, '<br/>')
            .replace(/----------------------------------------/g, '<hr/>');

        const devNome = devedorData?.nome || devedorData?.nomeDevedor || devedorData?.ccNome || "Nome não disponível";
        const devDoc = devedorData?.documento || devedorData?.cpfCnpj || devedorData?.ccDocumento || devedorData?.cnpj || devedorData?.cpf || "Documento não disponível";

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Resumo de Ocorrências</title>
                <style>
                    @media print {
                        @page { margin: 15mm 20mm; }
                        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
                    }
                    html, body { background: #fff !important; margin: 0 !important; padding: 15mm 20mm !important; box-sizing: border-box; }
                    body { font-family: 'Roboto', 'Calibri', 'Segoe UI', Helvetica, Arial, sans-serif; color: #000; font-size: 14px; }
                    .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #000; padding-bottom: 20px; }
                    .header img { max-width: 250px; margin-bottom: 15px; }
                    .header h1 { font-size: 18px; margin: 0 0 5px 0; color: #333; }
                    .header p { font-size: 12px; margin: 0; color: #666; }
                    .content { line-height: 1.6; }
                    hr { border: 0; border-top: 1px solid #ccc; margin: 15px 0; }
                </style>
            </head>
            <body>
                <div class="header">
                    <img src="${window.location.origin}${logoRal}" alt="Logo" onerror="this.src='${logoRal}'" style="max-width:250px;" />
                    <h1>${devNome}</h1>
                    <p style="font-weight: bold; color: #1a3b6c;">CPF/CNPJ: ${devDoc}</p>
                </div>
                <div class="content">
                    ${formattedTextHtml}
                </div>
            </body>
            </html>
        `);
        printWindow.document.close();
    };

    const handleOpenResumoIA = () => {
        const userOcorrencias = historico.filter(item => item.nomeUsuario && item.nomeUsuario.toLowerCase() !== "sistema" && item.nomeUsuario.toLowerCase() !== "robot" && !item.situacaoLabel?.toLowerCase().includes("importação"));
        
        if (userOcorrencias.length === 0) {
            errorAlert("Não há ocorrências de usuários para gerar relatório.");
            return;
        }

        // Função para mascarar dados sensíveis (LGPD) antes de mandar para a IA
        const sanitizeText = (text) => {
            if (!text) return "";
            return text
                .replace(/\b\d{3}\.\d{3}\.\d{3}-\d{2}\b/g, "***.***.***-**") // CPF formatado
                .replace(/\b\d{11}\b/g, "***********") // CPF sem formatação
                .replace(/\b\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}\b/g, "**.***/****-**") // CNPJ
                .replace(/\(\d{2}\)\s?\d{4,5}-\d{4}/g, "(**) *****-****") // Telefone formatado
                .replace(/\b\d{10,11}\b/g, "***********") // Telefone sem formatação
                .replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, "***@***.***"); // E-mail
        };
        
        let textToAi = "";
        userOcorrencias.forEach(item => {
            textToAi += `Data: ${formatarData(item.dataHora)} - Usuário: ${item.nomeUsuario} - Situação: ${item.situacaoLabel || 'Ocorrência'}\n`;
            if (item.observacao) {
                textToAi += `Obs: ${sanitizeText(item.observacao)}\n`;
            }
            textToAi += `\n`;
        });
        
        setGeneratingAi(true);
        post("/devedor/historico/summarize-ai", { 
            historicoText: textToAi,
            idDevedor: recordId
        })
            .then((res) => {
                if (res && res.resumo) {
                    const today = new Date().toLocaleDateString('pt-BR');
                    const finalTitle = `**Relatório de Andamento de Cobrança - Data: ${today}**\n\n`;
                    // Remove markdown bold se a IA por acaso colocou e garante o nosso título
                    let cleanResumo = res.resumo.replace(/^\*\*(.*?)\*\*\n+/g, ''); 
                    setResumoText(finalTitle + cleanResumo);
                    setOpenResumo(true);
                } else {
                    errorAlert("A IA não retornou um resumo válido.");
                }
            })
            .catch(() => {
                errorAlert("Erro ao gerar relatório com a Inteligência Artificial.");
            })
            .finally(() => {
                setGeneratingAi(false);
            });
    };

    return (
        <MDBox>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card sx={{ p: 2, boxShadow: "none", border: "1px solid", borderColor: "divider" }}>
                        <Grid container spacing={2} alignItems="flex-end">
                            <Grid item xs={12} md={3}>
                                <Autocomplete
                                    options={availableTiposOcorrencia}
                                    groupBy={(option) => option.finalGrupoLabel}
                                    renderGroup={(params) => (
                                        <GroupComponent key={params.key} params={params} darkMode={darkMode}>
                                            {params.children}
                                        </GroupComponent>
                                    )}
                                    getOptionLabel={(option) => option.nome || ""}
                                    value={selectedTipo}
                                    onChange={(event, newValue) => {
                                        setSelectedTipo(newValue);
                                        setDataAgendamento("");
                                    }}
                                    renderInput={(params) => <MDInput {...params} label="Tipo de Ocorrência" variant="standard" required InputLabelProps={{ shrink: true }} />}
                                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Autocomplete
                                    options={availableStatusList}
                                    getOptionLabel={(option) => option.ccNome || option.nome || ""}
                                    value={selectedStatus}
                                    onChange={(event, newValue) => {
                                        setSelectedStatus(newValue);
                                        setDataAgendamento("");
                                    }}
                                    renderInput={(params) => <MDInput {...params} label="Status" variant="standard" required InputLabelProps={{ shrink: true }} />}
                                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                                />
                            </Grid>
                            {selectedTipo && selectedTipo.permiteAgendamento && (
                                <Grid item xs={12} md={2}>
                                    <MDDatePicker
                                        value={dataAgendamento}
                                        onChange={(dates, dateStr) => {
                                            setDataAgendamento(dateStr);
                                        }}
                                        options={{
                                            enableTime: true,
                                            dateFormat: "Y-m-d\\TH:i",
                                            minDate: minDateStr,
                                            maxDate: maxDateStr,
                                            minuteIncrement: 15,
                                            time_24hr: true,
                                            disableMobile: true,
                                            onOpen: function(selectedDates, dateStr, instance) {
                                                if (instance && instance.calendarContainer) {
                                                    instance.calendarContainer.style.transform = 'scale(0.85)';
                                                    instance.calendarContainer.style.transformOrigin = 'top center';
                                                }
                                            }
                                        }}
                                        input={{
                                            label: "Data Agendamento",
                                            variant: "standard",
                                            fullWidth: true,
                                            required: true,
                                            InputLabelProps: { shrink: true }
                                        }}
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} md={selectedTipo && selectedTipo.permiteAgendamento ? 2 : 2.5}>
                                <Autocomplete
                                    freeSolo
                                    forcePopupIcon={true}
                                    options={availableTelefones}
                                    value={telefoneContato}
                                    onChange={(e, nv) => setTelefoneContato(nv || "")}
                                    onInputChange={(e, nv) => setTelefoneContato(nv || "")}
                                    renderInput={(params) => {
                                        const lowerStatus = selectedStatus?.nome?.toLowerCase() || selectedStatus?.ccNome?.toLowerCase() || '';
                                        const isPesquisaStatus = lowerStatus.includes('pesquisa') || lowerStatus.includes('não localizado') || lowerStatus.includes('nao localizado');
                                        return <MDInput {...params} label="Telefones Contatados" variant="standard" required={!isPesquisaStatus && !telefoneContato && !emailContato} InputLabelProps={{ shrink: true }} />;
                                    }}
                                />
                            </Grid>
                            {!(selectedTipo && selectedTipo.permiteAgendamento) && (
                                <Grid item xs={12} md={0.5} sx={{ display: 'flex', justifyContent: 'center', pb: 1 }}>
                                    <MDTypography variant="caption" color="text">ou</MDTypography>
                                </Grid>
                            )}
                            <Grid item xs={12} md={selectedTipo && selectedTipo.permiteAgendamento ? 2 : 3}>
                                <Autocomplete
                                    freeSolo
                                    forcePopupIcon={true}
                                    options={availableEmails}
                                    value={emailContato}
                                    onChange={(e, nv) => setEmailContato(nv || "")}
                                    onInputChange={(e, nv) => setEmailContato(nv || "")}
                                    renderInput={(params) => {
                                        const lowerStatus = selectedStatus?.nome?.toLowerCase() || selectedStatus?.ccNome?.toLowerCase() || '';
                                        const isPesquisaStatus = lowerStatus.includes('pesquisa') || lowerStatus.includes('não localizado') || lowerStatus.includes('nao localizado');
                                        return <MDInput {...params} label="E-mails Contatados" variant="standard" required={!isPesquisaStatus && !telefoneContato && !emailContato} InputLabelProps={{ shrink: true }} />;
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                {semDadosResponsavel ? (
                                    <MDBox sx={{ bgcolor: 'background.default', borderRadius: 1, height: '40px', width: '100%', mt: 2 }} />
                                ) : (
                                    <MDInput
                                        fullWidth
                                        label="Nome do Responsável"
                                        variant="standard"
                                        value={nomeResponsavel}
                                        onChange={(e) => setNomeResponsavel(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                )}
                            </Grid>
                            <Grid item xs={12} md={3}>
                                {semDadosResponsavel ? (
                                    <MDBox sx={{ bgcolor: 'background.default', borderRadius: 1, height: '40px', width: '100%', mt: 2 }} />
                                ) : (
                                    <MDInput
                                        fullWidth
                                        label="Função do Responsável"
                                        variant="standard"
                                        value={funcaoResponsavel}
                                        onChange={(e) => setFuncaoResponsavel(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                )}
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <FormControlLabel 
                                    control={<Checkbox checked={semDadosResponsavel} onChange={(e) => {
                                        setSemDadosResponsavel(e.target.checked);
                                        if (e.target.checked) {
                                            setNomeResponsavel("");
                                            setFuncaoResponsavel("");
                                        }
                                    }} size="small" />} 
                                    label={<MDTypography variant="caption" color="text">Sem dados</MDTypography>}
                                />
                            </Grid>
                            <Grid item xs={12} md={4} display="flex" justifyContent="flex-end">
                                <MDButton 
                                    variant="contained" 
                                    color="success" 
                                    fullWidth
                                    onClick={handleSalvarOcorrencia} 
                                    disabled={!isFormValid()}
                                >
                                    <Icon sx={{ mr: 1 }}>save</Icon> {salvando ? "Salvando..." : "Salvar Registro"}
                                </MDButton>
                            </Grid>

                            <Grid item xs={12}>
                                <MDInput
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    maxRows={15}
                                    variant="outlined"
                                    required
                                    placeholder="Exposição do fato com clareza..."
                                    value={novaOcorrencia}
                                    onChange={(e) => setNovaOcorrencia(e.target.value)}
                                />
                                <MDBox display="flex" flexDirection="column" gap={1} mt={1}>
                                    <MDBox display="flex" alignItems="center" gap={2}>
                                        <MDButton
                                            variant="outlined"
                                            color={darkMode ? "white" : "info"}
                                            size="small"
                                            component="label"
                                            startIcon={<Icon>attach_file</Icon>}
                                        >
                                            Anexar Documento(s)
                                            <input 
                                                type="file" 
                                                hidden 
                                                multiple
                                                onChange={(e) => {
                                                    const files = Array.from(e.target.files);
                                                    setAnexosOcorrencia(prev => [...prev, ...files]);
                                                    e.target.value = null; // reset to allow selecting the same file again if removed
                                                }} 
                                            />
                                        </MDButton>
                                        
                                        {anexosOcorrencia.length > 0 && (
                                            <MDBox sx={{ width: 250 }}>
                                                <MDSelect
                                                    name="tipoDocumento"
                                                    value={TIPOS_DOCUMENTO.map(t => ({ value: t.id, label: t.label })).find(o => o.value === selectedTipoDoc)}
                                                    onChange={(selected) => setSelectedTipoDoc(selected ? selected.value : 8)}
                                                    options={TIPOS_DOCUMENTO.map(t => ({ value: t.id, label: t.label }))}
                                                />
                                            </MDBox>
                                        )}
                                    </MDBox>
                                    
                                    {anexosOcorrencia.length > 0 && (
                                        <MDBox display="flex" flexWrap="wrap" gap={1} mt={1}>
                                            {anexosOcorrencia.map((file, idx) => (
                                                <Chip 
                                                    key={idx}
                                                    label={file.name}
                                                    onDelete={() => setAnexosOcorrencia(prev => prev.filter((_, i) => i !== idx))}
                                                    color="info"
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            ))}
                                        </MDBox>
                                    )}
                                </MDBox>
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>
                
                <Grid item xs={12}>
                    <Card sx={{ p: 2, height: "100%", boxShadow: "none", border: "1px solid", borderColor: "divider" }}>
                        <MDBox display="flex" flexWrap="wrap" alignItems="center" mb={2} gap={2}>
                                <MDInput
                                    type="date"
                                    label="Data Início"
                                    value={dataInicio}
                                    onChange={(e) => setDataInicio(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    sx={darkMode ? { "& input": { colorScheme: "dark" } } : {}}
                                />
                                <MDInput
                                    type="date"
                                    label="Data Fim"
                                    value={dataFim}
                                    onChange={(e) => setDataFim(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    sx={darkMode ? { "& input": { colorScheme: "dark" } } : {}}
                                />
                                <Autocomplete
                                    options={tiposOcorrencia}
                                    groupBy={(option) => option.grupoLabel}
                                    renderGroup={(params) => (
                                        <GroupComponent key={params.key} params={params} darkMode={darkMode}>
                                            {params.children}
                                        </GroupComponent>
                                    )}
                                    getOptionLabel={(option) => option.nome || ""}
                                    value={filtroTipo}
                                    onChange={(event, newValue) => setFiltroTipo(newValue)}
                                    renderInput={(params) => <MDInput {...params} label="Filtrar por Tipo" size="small" />}
                                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                                    sx={{ minWidth: 200, flex: 1 }}
                                />
                                <Autocomplete
                                    options={[OPTION_ALL_EQUIPES, ...equipesList]}
                                    getOptionLabel={(option) => option.nome || option.ccNome || ""}
                                    value={selectedEquipeFiltro}
                                    onChange={(event, newValue) => setSelectedEquipeFiltro(newValue || OPTION_ALL_EQUIPES)}
                                    renderInput={(params) => <MDInput {...params} label="Filtrar Equipe" size="small" />}
                                    isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                                    sx={{ minWidth: 180 }}
                                />
                                <FormControlLabel 
                                    control={<Checkbox checked={filtroImportacao} onChange={(e) => setFiltroImportacao(e.target.checked)} size="small" />} 
                                    label={<MDTypography variant="caption" color="text">Importações</MDTypography>}
                                />
                                {canSeeRelatorioIA && (
                                    <MDButton size="small" onClick={handleOpenResumoIA} color="secondary" variant="contained" disabled={generatingAi}>
                                        {generatingAi ? <><Icon sx={{ mr: 1, animation: 'spin 2s linear infinite' }}>autorenew</Icon> Gerando IA...</> : <><Icon sx={{ mr: 1 }}>smart_toy</Icon> Relatório IA</>}
                                    </MDButton>
                                )}
                                <MDButton size="small" onClick={handleOpenResumo} color="warning" variant="outlined">Resumo Textual</MDButton>
                                <MDButton size="small" onClick={() => setFiltro("TODOS")} color="primary" variant={filtro === "TODOS" ? "contained" : "outlined"}>Todos</MDButton>
                                <MDButton size="small" onClick={() => setFiltro("ALTERACAO")} color="info" variant={filtro === "ALTERACAO" ? "contained" : "outlined"}>Alterações</MDButton>
                                <MDButton size="small" onClick={() => setFiltro("OCORRENCIA")} color="success" variant={filtro === "OCORRENCIA" ? "contained" : "outlined"}>Ocorrências</MDButton>
                        </MDBox>
                        
                        <Divider />

                        <MDBox sx={{ maxHeight: 500, overflowY: "auto", pr: 2 }}>
                            {!recordId ? (
                                <MDBox p={3} textAlign="center">
                                    <MDTypography variant="body2" color="text">Salve o Cadastro Básico primeiro para visualizar e adicionar o histórico.</MDTypography>
                                </MDBox>
                            ) : loading ? (
                                <MDBox p={3}>
                                    <Skeleton height={80} />
                                    <Skeleton height={80} />
                                    <Skeleton height={80} />
                                </MDBox>
                            ) : filteredHistorico.length === 0 ? (
                                <MDBox p={3} textAlign="center">
                                    <MDTypography variant="body2" color="text">Nenhum registro encontrado para esta visão.</MDTypography>
                                </MDBox>
                            ) : (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={5}>
                                        <MDBox border="1px solid" borderColor="divider" borderRadius="md" sx={{ maxHeight: 350, overflowY: "auto", mb: 0 }}>
                                            <List dense disablePadding>
                                                {filteredHistorico.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
                                                    <ListItemButton 
                                                        key={item.id} 
                                                        selected={selectedItem?.id === item.id}
                                                        onClick={() => setSelectedItem(item)}
                                                        sx={{ py: 0.5, px: 1, borderBottom: "1px solid", borderColor: "divider" }}
                                                    >
                                                        <Icon sx={{ 
                                                            color: (item.situacaoLabel?.toLowerCase().includes("acordo") || item.situacaoLabel?.toLowerCase().includes("pagamento de parcela")) ? (darkMode ? '#FF60B5' : '#c2185b') : 
                                                                   item.situacaoLabel?.toLowerCase().includes("pagamento avulso") ? (darkMode ? '#66bb6a' : '#2e7d32') :
                                                                   item.cor === 'warning' ? '#fb8c00' : 
                                                                   item.cor === 'info' ? '#1A73E8' : '#4caf50', 
                                                            mr: 1, 
                                                            fontSize: '18px!important' 
                                                        }}>
                                                            {getIconBySituacao(item)}
                                                        </Icon>
                                                        <ListItemText 
                                                            primary={
                                                                <MDTypography 
                                                                    variant="caption" 
                                                                    fontWeight="medium" 
                                                                    sx={{ 
                                                                        lineHeight: 1,
                                                                        color: (item.situacaoLabel?.toLowerCase().includes("acordo") || item.situacaoLabel?.toLowerCase().includes("pagamento de parcela")) ? (darkMode ? '#FF60B5' : '#c2185b') : 
                                                                               item.situacaoLabel?.toLowerCase().includes("pagamento avulso") ? (darkMode ? '#66bb6a' : '#2e7d32') : undefined
                                                                    }}
                                                                >
                                                                    {item.situacaoLabel || "Ocorrência"}
                                                                </MDTypography>
                                                            } 
                                                            secondary={
                                                                <MDTypography variant="caption" color="text" sx={{ fontSize: '0.65rem' }}>
                                                                    {formatarData(item.dataHora)}
                                                                </MDTypography>
                                                            }
                                                            sx={{ my: 0 }}
                                                        />
                                                    </ListItemButton>
                                                ))}
                                            </List>
                                        </MDBox>
                                        <TablePagination
                                            component="div"
                                            count={filteredHistorico.length}
                                            page={page}
                                            onPageChange={(e, newPage) => setPage(newPage)}
                                            rowsPerPage={rowsPerPage}
                                            onRowsPerPageChange={(e) => {
                                                setRowsPerPage(parseInt(e.target.value, 10));
                                                setPage(0);
                                            }}
                                            labelRowsPerPage="Itens por pág:"
                                            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                                            rowsPerPageOptions={[10, 25, 50, 100]}
                                            sx={{
                                                color: darkMode ? "#fff" : "inherit",
                                                ".MuiTablePagination-toolbar": { minHeight: 36, paddingLeft: 1, paddingRight: 1 },
                                                ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": { fontSize: "0.75rem", margin: 0, color: darkMode ? "#fff" : "inherit" },
                                                ".MuiTablePagination-select": { fontSize: "0.75rem", color: darkMode ? "#fff" : "inherit" },
                                                ".MuiTablePagination-selectIcon": { color: darkMode ? "#fff" : "inherit" },
                                                ".MuiTablePagination-actions button": { color: darkMode ? "#fff" : "inherit" }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={7}>
                                        <Card variant="outlined" sx={{ p: 2, height: "100%", minHeight: 300, bgcolor: darkMode ? "#4B4F5C" : "background.default" }}>
                                            {selectedItem ? (
                                                <MDBox>
                                                    <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                                        <MDTypography 
                                                            variant="button" 
                                                            fontWeight="bold" 
                                                            sx={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center',
                                                                color: (selectedItem.situacaoLabel?.toLowerCase().includes("acordo") || selectedItem.situacaoLabel?.toLowerCase().includes("pagamento de parcela")) ? (darkMode ? '#FF60B5' : '#c2185b') : 
                                                                       selectedItem.situacaoLabel?.toLowerCase().includes("pagamento avulso") ? (darkMode ? '#66bb6a' : '#2e7d32') : undefined
                                                            }}
                                                            color={(selectedItem.situacaoLabel?.toLowerCase().includes("acordo") || selectedItem.situacaoLabel?.toLowerCase().includes("pagamento de parcela") || selectedItem.situacaoLabel?.toLowerCase().includes("pagamento avulso")) ? undefined : (darkMode ? "white" : getColorBySituacao(selectedItem))}
                                                        >
                                                            <Icon sx={{ mr: 0.5, fontSize: '18px!important', color: (darkMode && !selectedItem.situacaoLabel?.toLowerCase().includes("acordo") && !selectedItem.situacaoLabel?.toLowerCase().includes("pagamento")) ? '#ffffff' : 'inherit' }}>{getIconBySituacao(selectedItem)}</Icon>
                                                            {selectedItem.situacaoLabel || "Ocorrência"}
                                                        </MDTypography>
                                                        {podeExcluirOcorrencia && (
                                                            <MDButton color="error" size="small" variant="text" onClick={() => handleDeleteOcorrencia(selectedItem.id)}>
                                                                <Icon sx={{ mr: 0.5 }}>delete</Icon> Excluir
                                                            </MDButton>
                                                        )}
                                                    </MDBox>
                                                    <Divider sx={{ my: 1 }} />
                                                    <Grid container spacing={1} mb={1}>
                                                        <Grid item xs={6}>
                                                            <MDTypography variant="caption" color={darkMode ? "white" : "text"} fontWeight="bold">Data/Hora: </MDTypography>
                                                            <MDTypography variant="caption" fontWeight="regular" color={darkMode ? "white" : "dark"}>{formatarData(selectedItem.dataHora)}</MDTypography>
                                                        </Grid>
                                                        <Grid item xs={6}>
                                                            <MDTypography variant="caption" color={darkMode ? "white" : "text"} fontWeight="bold">Usuário: </MDTypography>
                                                            <MDTypography variant="caption" fontWeight="regular" color={darkMode ? "white" : "dark"}>
                                                                {selectedItem.nomeUsuario || "Sistema"}
                                                                {selectedItem.nomeEquipe ? ` [${selectedItem.nomeEquipe}]` : selectedItem.nomePerfil ? ` (${selectedItem.nomePerfil})` : ""}
                                                            </MDTypography>
                                                        </Grid>
                                                    </Grid>
                                                    <MDBox>
                                                        <MDTypography variant="caption" color={darkMode ? "white" : "text"} fontWeight="bold">Observação:</MDTypography>
                                                        <MDBox sx={{ p: 1, bgcolor: darkMode ? "#424550" : "background.default", border: "1px solid", borderColor: darkMode ? "#606472" : "divider", borderRadius: 1, mt: 0.5, minHeight: 100, color: darkMode ? "#fff" : "inherit" }}>
                                                            {renderObservacao(selectedItem.observacao)}
                                                        </MDBox>
                                                    </MDBox>
                                                    
                                                    {/* Se a ocorrência tem documento anexado */}
                                                    {(() => {
                                                        let attachedDocs = [];
                                                        if (selectedItem) {
                                                            attachedDocs = documentosFicha.filter(d => d.idHistorico === selectedItem.id);
                                                            
                                                            if (attachedDocs.length === 0) {
                                                                let minDiff = Infinity;
                                                                let closestDoc = null;
                                                                documentosFicha.forEach(d => {
                                                                    if (!d.idHistorico && (d.tipoDocumento === 8 || (d.observacao && d.observacao.toLowerCase().includes("ocorrência")))) {
                                                                        const diffMs = Math.abs(new Date(d.dataUpload).getTime() - new Date(selectedItem.dataHora).getTime());
                                                                        if (diffMs < 1200000) { // 20 min margin
                                                                            let isClosest = true;
                                                                            for (let occ of historico) {
                                                                                if (occ.id !== selectedItem.id) {
                                                                                    const otherDiff = Math.abs(new Date(d.dataUpload).getTime() - new Date(occ.dataHora).getTime());
                                                                                    if (otherDiff < diffMs || (otherDiff === diffMs && occ.id > selectedItem.id)) {
                                                                                        isClosest = false;
                                                                                        break;
                                                                                    }
                                                                                }
                                                                            }
                                                                            if (isClosest && diffMs < minDiff) {
                                                                                minDiff = diffMs;
                                                                                closestDoc = d;
                                                                            }
                                                                        }
                                                                    }
                                                                });
                                                                if (closestDoc) attachedDocs.push(closestDoc);
                                                            }
                                                        }

                                                        if (attachedDocs.length > 0) {
                                                            return (
                                                                <MDBox mt={1.5}>
                                                                    {attachedDocs.map((docItem, idx) => (
                                                                        <MDBox key={idx} mb={1} p={1.5} borderRadius={1} border="1px dashed" borderColor="info.main" bgcolor={darkMode ? "#4B4F5C" : "#e8f0fe"} display="flex" alignItems="center" justifyContent="space-between">
                                                                            <MDBox display="flex" alignItems="center" gap={1}>
                                                                                <Icon color="info">attach_file</Icon>
                                                                                <MDBox>
                                                                                    <MDTypography variant="button" fontWeight="bold" display="block">
                                                                                        {attachedDocs.length > 1 ? `Documento Anexado na Ocorrência (${idx + 1}/${attachedDocs.length})` : "Documento Anexado na Ocorrência"}
                                                                                    </MDTypography>
                                                                                    <MDTypography variant="caption" color="text">{docItem.nomeArquivo}</MDTypography>
                                                                                </MDBox>
                                                                            </MDBox>
                                                                            {docItem.id ? (
                                                                                <MDButton size="small" color="info" variant="contained" startIcon={<Icon>download</Icon>} onClick={() => getFile(`/dossie/download-file/${docItem.id}`, docItem.nomeArquivo)}>
                                                                                    Baixar Anexo
                                                                                </MDButton>
                                                                            ) : (
                                                                                <Chip label="Disponível na aba Documentos" size="small" color="info" />
                                                                            )}
                                                                        </MDBox>
                                                                    ))}
                                                                </MDBox>
                                                            );
                                                        }
                                                        return null;
                                                    })()}
                                                </MDBox>
                                            ) : (
                                                <MDBox display="flex" justifyContent="center" alignItems="center" height="100%">
                                                    <MDTypography variant="body2" color="text">Selecione uma ocorrência na lista para visualizar os detalhes.</MDTypography>
                                                </MDBox>
                                            )}
                                        </Card>
                                    </Grid>
                                </Grid>
                            )}
                        </MDBox>
                    </Card>
                </Grid>
            </Grid>

            <Dialog 
                open={openResumo} 
                onClose={() => setOpenResumo(false)} 
                fullWidth 
                maxWidth="md"
                sx={{ zIndex: 1050 }}
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? "#4B4F5C" : "#fff",
                        color: darkMode ? "#fff" : "inherit"
                    }
                }}
            >
                <DialogTitle sx={{ color: darkMode ? "#fff" : "inherit", display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Resumo de Ocorrências
                    {generatingAi && <CircularProgress size={20} color="secondary" />}
                </DialogTitle>
                <DialogContent>
                    <MDInput
                        multiline
                        minRows={10}
                        maxRows={20}
                        fullWidth
                        value={resumoText}
                        onChange={(e) => setResumoText(e.target.value)}
                        placeholder="O resumo gerado aparecerá aqui..."
                        sx={{ 
                            "& .MuiInputBase-input": { 
                                fontSize: "0.85rem",
                                lineHeight: 1.5,
                                fontFamily: "sans-serif"
                            } 
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ justifyContent: "space-between", width: "100%" }}>
                    <MDBox>
                        <MDButton onClick={handleExportResumo} color="primary" startIcon={<Icon>picture_as_pdf</Icon>}>
                            Exportar Documento
                        </MDButton>
                    </MDBox>
                    <MDBox>
                        <MDButton onClick={() => {
                            navigator.clipboard.writeText(resumoText);
                            successAlert("Resumo copiado para a área de transferência!");
                        }} color="info" sx={{ mr: 1 }}>Copiar Texto</MDButton>
                        <MDButton onClick={() => setOpenResumo(false)} color="dark">Fechar</MDButton>
                    </MDBox>
                </DialogActions>
            </Dialog>
        </MDBox>
    );
}
